import { chipClasses, Components, Theme } from '@mui/material';
import { Props } from '../../components/badge/types';
import { FigmaColorsType } from '../../shared-types/theme/palette';
import getPrefixedClassName from '../../utils/getPrefixClassNames';

export const customShapeClasses = {
  fullRounded: 'fullRoundedBadge',
  squareRounded: 'squareRoundedBadge',
} as const;

type ReturnType = Record<'MuiChip', Components['MuiChip']>;

const FULLY_ROUNDED = 9999;

export default function Chip(theme: Theme): ReturnType {
  return {
    MuiChip: {
      defaultProps: {
        color: 'grey',
        variant: 'outlined',
        size: 'medium',
      },
      variants: [
        {
          props: {
            size: 'small',
          },
          style: {
            height: theme.spacing(5.5),
            [`&.${chipClasses.root}:has(svg)`]: {
              padding: theme.spacing(0.5, 2),
            },
            [`.${[chipClasses.label]}`]: {
              ...theme.typography.textXs,
              fontWeight: theme.typography.fontWeightMedium,
              padding: theme.spacing(0.5, 2),
            },
          },
        },
        {
          props: {
            size: 'medium',
          },
          style: {
            height: theme.spacing(6),
            [`&.${chipClasses.root}:has(svg)`]: {
              padding: theme.spacing(0.5, 2.5),
            },
            [`.${[chipClasses.label]}`]: {
              ...theme.typography.textSm,
              fontWeight: theme.typography.fontWeightMedium,
              padding: theme.spacing(0.5, 2.5),
            },
          },
        },
        {
          props: {
            size: 'large',
          },
          style: {
            height: theme.spacing(7),
            [`&.${chipClasses.root}:has(svg)`]: {
              padding: theme.spacing(1, 3),
            },
            [`.${[chipClasses.label]}`]: {
              ...theme.typography.textSm,
              fontWeight: theme.typography.fontWeightMedium,
              padding: theme.spacing(1, 3),
            },
          },
        },
      ],
      styleOverrides: {
        root: ({ ownerState: { color, variant } }) => {
          return {
            [`&.${chipClasses.root}:has(svg) .${chipClasses.label}`]: {
              padding: 0,
            },
            [`&.${chipClasses.root}:has(svg) svg`]: {
              margin: 0,
            },
            [`&.${chipClasses.root}:has(svg)`]: {
              gap: theme.spacing(1.5),
            },
            [`&.${getPrefixedClassName(customShapeClasses.fullRounded)}`]: {
              borderRadius: FULLY_ROUNDED,
            },
            [`&.${getPrefixedClassName(customShapeClasses.squareRounded)}`]: {
              borderRadius: theme.shape.borderRadius * 1.5,
            },
            [`&.${getPrefixedClassName(color)}`]: createBadgeStylesByVariant({
              color,
              variant,
              theme,
            }),
            [`.${chipClasses.avatar}`]: {
              width: theme.spacing(4),
              height: theme.spacing(4),
            },
            [`.${chipClasses.icon}`]: {
              width: theme.spacing(3),
              height: theme.spacing(3),
            },
          };
        },
      },
    },
  };
}

type FilledType = {
  borderColor: FigmaColorsType;
  bg: FigmaColorsType;
  color: FigmaColorsType;
};

type OutlinedType = {
  color: FigmaColorsType;
  borderColor: FigmaColorsType;
};

function createBadgeStylesByVariant({
  color = 'grey',
  variant = 'filled',
  theme,
}: {
  color: Props['color'];
  variant: Props['variant'];
  theme: Theme;
}) {
  const colors: {
    [key in NonNullable<Props['variant']>]: {
      [key in NonNullable<Props['color']>]: FilledType | OutlinedType;
    };
  } = {
    filled: {
      grey: {
        bg: 'Component colors/Utility/Gray/utility-gray-50',
        borderColor: 'Component colors/Utility/Gray/utility-gray-200',
        color: 'Component colors/Utility/Gray/utility-gray-800',
      },
      brand: {
        bg: 'Component colors/Utility/Gray/utility-gray-50',
        borderColor: 'Component colors/Utility/Gray/utility-gray-200',
        color: 'Component colors/Utility/Brand/utility-brand-700',
      },
      error: {
        bg: 'Component colors/Utility/Error/utility-error-50',
        borderColor: 'Component colors/Utility/Error/utility-error-200',
        color: 'Component colors/Utility/Error/utility-error-700',
      },
      warning: {
        bg: 'Component colors/Utility/Warning/utility-warning-50',
        borderColor: 'Component colors/Utility/Warning/utility-warning-200',
        color: 'Component colors/Utility/Warning/utility-warning-700',
      },
      success: {
        bg: 'Component colors/Utility/Success/utility-success-50',
        borderColor: 'Component colors/Utility/Success/utility-success-200',
        color: 'Component colors/Utility/Success/utility-success-700',
      },
      greyBlue: {
        bg: 'Component colors/Utility/Gray blue/utility-gray-blue-50',
        borderColor: 'Component colors/Utility/Gray blue/utility-gray-blue-200',
        color: 'Component colors/Utility/Gray blue/utility-gray-blue-700',
      },
      blueLight: {
        bg: 'Component colors/Utility/Blue light/utility-blue-light-50',
        borderColor:
          'Component colors/Utility/Blue light/utility-blue-light-200',
        color: 'Component colors/Utility/Blue light/utility-blue-light-700',
      },
      blue: {
        bg: 'Component colors/Utility/Blue/utility-blue-50',
        borderColor: 'Component colors/Utility/Blue/utility-blue-200',
        color: 'Component colors/Utility/Blue/utility-blue-700',
      },
      indigo: {
        bg: 'Component colors/Utility/Indigo/utility-indigo-50',
        borderColor: 'Component colors/Utility/Indigo/utility-indigo-200',
        color: 'Component colors/Utility/Indigo/utility-indigo-700',
      },
      purple: {
        bg: 'Component colors/Utility/Purple/utility-purple-50',
        borderColor: 'Component colors/Utility/Purple/utility-purple-200',
        color: 'Component colors/Utility/Purple/utility-purple-700',
      },
      pink: {
        bg: 'Component colors/Utility/Pink/utility-pink-50',
        borderColor: 'Component colors/Utility/Pink/utility-pink-200',
        color: 'Component colors/Utility/Pink/utility-pink-700',
      },
      orange: {
        bg: 'Component colors/Utility/Orange/utility-orange-50',
        borderColor: 'Component colors/Utility/Orange/utility-orange-200',
        color: 'Component colors/Utility/Orange/utility-orange-700',
      },
    },
    outlined: {
      grey: {
        borderColor: 'Component colors/Utility/Gray/utility-gray-600',
        color: 'Component colors/Utility/Gray/utility-gray-800',
      },
      brand: {
        borderColor: 'Component colors/Utility/Brand/utility-brand-600',
        color: 'Component colors/Utility/Brand/utility-brand-700',
      },
      error: {
        borderColor: 'Component colors/Utility/Error/utility-error-600',
        color: 'Component colors/Utility/Error/utility-error-700',
      },
      warning: {
        borderColor: 'Component colors/Utility/Warning/utility-warning-600',
        color: 'Component colors/Utility/Warning/utility-warning-700',
      },
      success: {
        borderColor: 'Component colors/Utility/Success/utility-success-600',
        color: 'Component colors/Utility/Success/utility-success-700',
      },
      greyBlue: {
        borderColor: 'Component colors/Utility/Gray blue/utility-gray-blue-600',
        color: 'Component colors/Utility/Gray blue/utility-gray-blue-700',
      },
      blueLight: {
        borderColor:
          'Component colors/Utility/Blue light/utility-blue-light-600',
        color: 'Component colors/Utility/Blue light/utility-blue-light-700',
      },
      blue: {
        borderColor: 'Component colors/Utility/Blue/utility-blue-600',
        color: 'Component colors/Utility/Blue/utility-blue-700',
      },
      indigo: {
        borderColor: 'Component colors/Utility/Indigo/utility-indigo-600',
        color: 'Component colors/Utility/Indigo/utility-indigo-700',
      },
      purple: {
        borderColor: 'Component colors/Utility/Purple/utility-purple-600',
        color: 'Component colors/Utility/Purple/utility-purple-700',
      },
      pink: {
        borderColor: 'Component colors/Utility/Pink/utility-pink-600',
        color: 'Component colors/Utility/Pink/utility-pink-700',
      },
      orange: {
        borderColor: 'Component colors/Utility/Orange/utility-orange-600',
        color: 'Component colors/Utility/Orange/utility-orange-700',
      },
    },
  };
  const colorKey = colors[variant][color];
  if (variant === 'filled' && 'bg' in colorKey) {
    return {
      backgroundColor: theme.palette[colorKey.bg],
      color: theme.palette[colorKey.color],
      border: `1px solid ${theme.palette[colorKey.borderColor]}`,
      [`&.${chipClasses.avatar}`]: {
        color: theme.palette[colorKey.color],
      },
      [`.${chipClasses.icon}`]: {
        color: theme.palette[colorKey.color],
        width: theme.spacing(3),
        height: theme.spacing(3),
      },
      [`.${chipClasses.deleteIcon}`]: {
        color: theme.palette[colorKey.color],
        width: theme.spacing(3),
        height: theme.spacing(3),
      },
    };
  }

  return {
    color: theme.palette[colorKey.color],
    border: `1px solid ${theme.palette[colorKey.borderColor]}`,
    [`&.${chipClasses.avatar}`]: {
      color: theme.palette[colorKey.color],
    },
    [`.${chipClasses.icon}`]: {
      color: theme.palette[colorKey.color],
      width: theme.spacing(3),
      height: theme.spacing(3),
    },
    [`.${chipClasses.deleteIcon}`]: {
      color: theme.palette[colorKey.color],
      width: theme.spacing(3),
      height: theme.spacing(3),
    },
  };
}
